import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, Plus, Loader2, Sparkles } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ActivityPostCard } from '../components/common/ActivityPostCard';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ActivityPage() {
  const navigate = useNavigate();
  const { isAuthenticated, api } = useAuth();
  const { subscribe } = useWebSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [postForm, setPostForm] = useState({
    type: 'offer',
    title: '',
    description: '',
    category_id: '',
    duration_hours: 1,
    exchange_for: '',
    location: 'Ottignies-Louvain-la-Neuve',
  });

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [searchParams]);

  useEffect(() => {
    // Subscribe to real-time activity updates
    const unsubscribe = subscribe('new_activity', (data) => {
      setPosts((prev) => [data.data, ...prev]);
      toast.info('Nouvelle annonce publiée !');
    });

    return () => unsubscribe();
  }, [subscribe]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('type')) params.append('type', searchParams.get('type'));
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      params.append('limit', '50');

      const response = await axios.get(`${API_URL}/activity?${params.toString()}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      await api.post('/activity', postForm);
      toast.success('Annonce publiée avec succès !');
      setPostModalOpen(false);
      setPostForm({
        type: 'offer',
        title: '',
        description: '',
        category_id: '',
        duration_hours: 1,
        exchange_for: '',
        location: 'Ottignies-Louvain-la-Neuve',
      });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    }
  };

  const handleContact = (userId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/messages?user=${userId}`);
  };

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Activité</h1>
            <p className="text-muted-foreground">
              Découvrez les dernières demandes et propositions de la communauté
            </p>
          </div>
          <Dialog open={postModalOpen} onOpenChange={setPostModalOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                Publier une annonce
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Publier une annonce</DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePostSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant={postForm.type === 'offer' ? 'default' : 'outline'}
                    className="rounded-xl"
                    onClick={() => setPostForm({ ...postForm, type: 'offer' })}
                  >
                    Je propose
                  </Button>
                  <Button
                    type="button"
                    variant={postForm.type === 'request' ? 'default' : 'outline'}
                    className="rounded-xl"
                    onClick={() => setPostForm({ ...postForm, type: 'request' })}
                  >
                    Je recherche
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-title">Titre *</Label>
                  <Input
                    id="post-title"
                    value={postForm.title}
                    onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                    placeholder="Ex: Cours de guitare pour débutants"
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-category">Catégorie *</Label>
                  <Select
                    value={postForm.category_id}
                    onValueChange={(value) => setPostForm({ ...postForm, category_id: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-description">Description</Label>
                  <Textarea
                    id="post-description"
                    value={postForm.description}
                    onChange={(e) => setPostForm({ ...postForm, description: e.target.value })}
                    placeholder="Décrivez votre offre ou demande..."
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="post-duration">Durée (heures) *</Label>
                    <Input
                      id="post-duration"
                      type="number"
                      min="1"
                      value={postForm.duration_hours}
                      onChange={(e) => setPostForm({ ...postForm, duration_hours: parseInt(e.target.value) })}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="post-location">Lieu</Label>
                    <Input
                      id="post-location"
                      value={postForm.location}
                      onChange={(e) => setPostForm({ ...postForm, location: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-exchange">En échange de (optionnel)</Label>
                  <Input
                    id="post-exchange"
                    value={postForm.exchange_for}
                    onChange={(e) => setPostForm({ ...postForm, exchange_for: e.target.value })}
                    placeholder="Ex: Aide au jardinage"
                    className="rounded-xl"
                  />
                </div>

                <Button type="submit" className="w-full btn-primary">
                  Publier l'annonce
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Select
            value={typeFilter || 'all'}
            onValueChange={(value) => {
              setTypeFilter(value);
              updateFilters('type', value);
            }}
          >
            <SelectTrigger className="w-[160px] rounded-xl" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tout</SelectItem>
              <SelectItem value="offer">Propositions</SelectItem>
              <SelectItem value="request">Demandes</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={categoryFilter || 'all'}
            onValueChange={(value) => {
              setCategoryFilter(value);
              updateFilters('category', value);
            }}
          >
            <SelectTrigger className="w-[200px] rounded-xl" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.slug}>
                  {cat.icon} {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="activity-grid">
            {posts.map((post) => (
              <ActivityPostCard key={post.id} post={post} onContact={handleContact} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Sparkles className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune annonce pour le moment</h3>
            <p className="text-muted-foreground mb-6">
              Soyez le premier à publier une annonce !
            </p>
            <Button className="btn-primary" onClick={() => setPostModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Publier une annonce
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
